import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import { getToken, onTokenChange } from "./tokenStore";

// Uma conexão só para o app inteiro.
//
// Antes cada tela chamava `io()` por conta própria. O socket.io-client 4 não
// reaproveita o Manager quando o namespace já existe no cache — ele abre um
// novo — então 22 telas viravam 22 WebSockets, e cada `disconnect()` de tela
// que desmontava derrubava um handshake ainda em andamento. Daí a enxurrada de
// "WebSocket is closed before the connection is established" no console.
//
// Aqui a conexão é única e contada por referência: cada consumidor recebe um
// controle que só enxerga os próprios ouvintes. Ao desmontar, ele remove os
// que registrou; a conexão real só cai quando o último consumidor sai.

let socketReal = null;
let tokenDaConexao = null;
let consumidores = 0;

// Separa o endereço da API em origem + caminho do Socket.IO.
//
// Quando a API mora num subcaminho ("https://dominio/api/"), passar a URL
// inteira para o io() não funciona: o socket.io-client lê o caminho como
// *namespace*, não como path do transporte, e o servidor responde "Invalid
// namespace". O caminho tem que ir na opção `path`.
//
// Com a API na raiz ("http://localhost:8080/") o resultado é "/socket.io" —
// exatamente o padrão de antes.
const enderecoDoSocket = () => {
	const base = String(getBackendUrl() || "");

	try {
		const url = new URL(base, window.location.origin);
		const prefixo = url.pathname.replace(/\/+$/, "");

		return { origem: url.origin, path: `${prefixo}/socket.io` };
	} catch {
		return { origem: base, path: "/socket.io" };
	}
};

const lerToken = () => getToken();

// Sem token não há o que ouvir: o backend recusa o handshake e o cliente fica
// tentando de novo. É o caso da tela de login.
const controleVazio = {
	on: () => controleVazio,
	off: () => controleVazio,
	emit: () => controleVazio,
	disconnect: () => {}
};

// Mantém o handshake da próxima reconexão com o token válido.
//
// O token só é verificado no handshake. Sem isto, uma reconexão depois dos 15
// minutos de validade — queda de rede, notebook que dormiu, servidor que
// reiniciou — repetia o token vencido, era recusada, e o cliente tentava de
// novo com o mesmo token para sempre: a tela parava de receber QR Code,
// mensagem nova e mudança de status, sem nenhum aviso.
const aplicarToken = (token) => {
	if (!socketReal || !token) return;

	tokenDaConexao = token;

	if (socketReal.io?.opts?.query) {
		socketReal.io.opts.query.token = token;
	}
	if (socketReal.auth) {
		socketReal.auth.token = token;
	}
};

function connectToSocket() {
	const token = lerToken();

	if (!token) return controleVazio;

	// Token diferente com ninguém pendurado significa outra sessão: descarta a
	// conexão antiga. Com consumidores ativos não se mexe — o backend só valida
	// o token no handshake, e derrubar a conexão aqui deixaria a lista de
	// tickets e as notificações mudas até a tela remontar. A renovação de token
	// acontece a cada poucos minutos; ela não pode custar a conexão de todos.
	if (socketReal && tokenDaConexao !== token && consumidores <= 0) {
		socketReal.disconnect();
		socketReal = null;
	}

	// Conexão viva com token renovado: só alinha o que vai no próximo
	// handshake, sem derrubar a atual. (O caminho normal é o ouvinte do
	// tokenStore lá embaixo; isto cobre quem monta entre uma renovação e a
	// outra.)
	if (socketReal && tokenDaConexao !== token) {
		aplicarToken(token);
	}

	if (!socketReal) {
		tokenDaConexao = token;
		const { origem, path } = enderecoDoSocket();

		socketReal = openSocket(origem, {
			path,
			transports: ["websocket", "polling"],
			query: { token }
		});
	}

	consumidores += 1;

	const meus = [];
	let ativo = true;

	const controle = {
		on(evento, handler) {
			if (!ativo) return controle;

			meus.push([evento, handler]);
			socketReal.on(evento, handler);

			// As telas entram nas salas do Socket.IO dentro do "connect"
			// (`socket.on("connect", () => socket.emit("joinChatBox", id))`).
			// Quando cada tela abria a própria conexão isso sempre acontecia;
			// com a conexão compartilhada, quem chega depois encontra o socket
			// já conectado e o evento nunca mais dispara — a tela não entra na
			// sala e para de receber atualização em tempo real. Dispara na
			// hora, num tick à parte para o handler não rodar antes de o
			// componente terminar de montar.
			if (evento === "connect" && socketReal.connected) {
				setTimeout(() => {
					if (ativo) handler();
				}, 0);
			}

			return controle;
		},
		off(evento, handler) {
			const i = meus.findIndex(([e, h]) => e === evento && h === handler);
			if (i >= 0) meus.splice(i, 1);
			if (socketReal) socketReal.off(evento, handler);
			return controle;
		},
		emit(...args) {
			if (ativo && socketReal) socketReal.emit(...args);
			return controle;
		},
		// Mantém o nome que as telas já usam. Não derruba a conexão dos outros:
		// só larga os ouvintes deste consumidor.
		disconnect() {
			if (!ativo) return;
			ativo = false;

			meus.forEach(([evento, handler]) => {
				if (socketReal) socketReal.off(evento, handler);
			});
			meus.length = 0;

			consumidores -= 1;

			if (consumidores <= 0 && socketReal) {
				socketReal.disconnect();
				socketReal = null;
				tokenDaConexao = null;
				consumidores = 0;
			}
		}
	};

	return controle;
}

// O tokenStore avisa sozinho quando o token muda (login, renovação, logout).
onTokenChange(aplicarToken);

// Celular que dormiu ou troca de app: ao voltar ao foco, o socket costuma ter
// caído. Força a reconexão na hora — com o token já renovado pelo useAuth — em
// vez de esperar o backoff. Sem isto, mensagem e status novos demoravam demais
// a aparecer no PWA.
if (typeof document !== "undefined") {
	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible" && socketReal && !socketReal.connected) {
			socketReal.connect();
		}
	});
}

export default connectToSocket;
